import React, { useEffect } from "react";
import { Box, Button, CircularProgress, ThemeProvider, createTheme, ThemeOptions, Typography } from "@mui/material";
import type { Theme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { setLoading, setBrandConfig } from "@store/slices/brandSlice";
import type { RootState } from "@store/store";
import { useLazyGetThemeConfigQuery } from "@api/catalogs";
import { AppInterfaceConfig, AppThemePalette, ThemeApiStructure } from "@interfaces/theme";
import { themeBase } from "@theme/_themeBase";
import { getBaseUrl } from "@helpers/funciones";

const defaultTheme: AppThemePalette = {
  primary: {
    main: "#528CD6",
    dark: "#132B52",
    light: "#A3D4E8",
    base: "#E3F2F8",
    accent: "#F2704F",
  },
  components: {},
};

const defaultImageList: string[] = [
  "lendia/portal/lateral/imagen_1.webp",
  "lendia/portal/lateral/imagen_2.webp",
  "lendia/portal/lateral/imagen_3.webp",
  "lendia/portal/lateral/imagen_4.webp",
  "lendia/portal/lateral/imagen_5.webp",
  "lendia/portal/lateral/imagen_6.webp",
  "lendia/portal/lateral/imagen_7.webp",
  "lendia/portal/lateral/imagen_8.webp",
  "lendia/portal/lateral/imagen_9.webp",
  "lendia/portal/lateral/imagen_10.webp",
];

const defaultTextList: string[] = [
  "Así de fácil: solicita, compara, avanza",
  "Tu crédito, tu elección, tu crecimiento",
  "Crédito simple, procesos simples",
  "Avanza con las mejores condiciones del mercado",
  "Compara y decide: tu crédito, tus reglas",
  "Un solo lugar, todas las opciones de financiamiento",
  "Crecer nunca fue tan sencillo",
  "Descubre la mejor solución para tu PyME",
  "El poder de elegir está en tus manos",
  "Da el paso que tu empresa necesita",
];

const createPaletteConfig = (response: ThemeApiStructure): AppThemePalette => {
  const newPalette = {
    ...response.palette.palette,
  };
  return newPalette;
};

const createInterfaceConfig = (response: ThemeApiStructure): AppInterfaceConfig => {
  const newInterface = {
    ...response.palette.interface,
    url_home: response.palette.interface.url_home || "/solicita_tu_credito",
    url_privacy_notice: response.url_privacy_notice || "/aviso_de_privacidad",
    url_terms_and_conditions: response.url_terms_and_conditions || "/terminos_y_condiciones",
    lateral: response.palette.interface.lateral
      ? {
          ...response.palette.interface.lateral,
          image_list: response.palette.interface.lateral.image_list || defaultImageList,
          text_list: response.palette.interface.lateral.text_list || defaultTextList,
        }
      : undefined,
  };

  return newInterface;
};

const createDynamicTheme = (paletteConfig: AppThemePalette) => {
  const themeOptions: ThemeOptions = {
    palette: {
      primary: {
        main: paletteConfig.primary.main,
        dark: paletteConfig.primary.dark,
        light: paletteConfig.primary.light,
        base: paletteConfig.primary.base,
        accent: paletteConfig.primary.accent,
      },
      components: {
        slider: paletteConfig.components?.slider,
        progressBar: paletteConfig.components?.progressBar,
        buttonSelect: paletteConfig.components?.buttonSelect,
      },
    },
  };
  return createTheme(themeBase, themeOptions);
};

interface DynamicThemeProviderProps {
  children: React.ReactNode;
}

export default function DynamicThemeProvider({ children }: DynamicThemeProviderProps) {
  const dispatch = useDispatch();

  const { loading } = useSelector((state: RootState) => state.brand);

  const [getThemeConfig] = useLazyGetThemeConfigQuery();
  const [customTheme, setCustomTheme] = React.useState<Theme | null>(null);
  const [hasError, setHasError] = React.useState(false);

  const fetchData = React.useCallback(async () => {
    try {
      setHasError(false);
      dispatch(setLoading(true));

      let fullUrl = window.location.href;
      if (fullUrl.includes("localhost")) {
        fullUrl = import.meta.env.VITE_LOCALHOST_THEME_URL;
      }

      if (!fullUrl) {
        throw new Error("Base URL is not valid");
      }

      const baseUrl = getBaseUrl(fullUrl);
      const response = await getThemeConfig(baseUrl).unwrap();

      const newPalette = createPaletteConfig(response);
      const newInterface = createInterfaceConfig(response);

      dispatch(
        setBrandConfig({
          id: response.id,
          name: response.name,
          code: response.code,
          palette: newPalette,
          interface: newInterface,
        }),
      );
      setCustomTheme(createDynamicTheme(newPalette));
    } catch (error) {
      console.error("Error fetching theme config", error);
      setHasError(true);
    } finally {
      dispatch(setLoading(false));
    }
  }, [getThemeConfig, dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (hasError) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 3,
          bgcolor: "#fff",
          px: 2,
        }}
      >
        <Typography variant="h1" fontWeight="700" color="#000">
          Oops!
        </Typography>
        <Typography variant="h4" fontWeight="bold" color="#000">
          Ocurrió un error al procesar la información
        </Typography>
        <Button
          variant="contained"
          onClick={fetchData}
          sx={{
            px: 3,
            py: 1.5,
          }}
        >
          Reintentar
        </Button>
      </Box>
    );
  }

  if (loading || !customTheme) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          gap: 2,
        }}
      >
        <CircularProgress
          size={100}
          thickness={4}
          sx={{
            color: defaultTheme.primary.main,
            "& .MuiCircularProgress-circle": {
              strokeLinecap: "round",
            },
          }}
        />
        <Typography
          variant="body2"
          sx={{
            color: defaultTheme.primary.dark,
            fontWeight: 500,
            letterSpacing: "0.02em",
          }}
        >
          Cargando...
        </Typography>
      </Box>
    );
  }

  return <ThemeProvider theme={customTheme}>{children}</ThemeProvider>;
}
